import { OutgoingSearchResult } from "../types";
import { LLMRecipeOutgoing, LLMMealPlanOutgoing } from "../types/mealPlanning";
import { toTitleCase } from "./textFormatting";

export interface SimplifiedIngredient {
    name: string;
    brand?: string;
    quantityAndPortion: string; // e.g., "6 tbsp"
    totalWeightGrams: number;
    macros: {
        calories: number;
        protein: number;
        carbs: number;
        fat: number;
        fiber: number;
    };
}

export interface SimplifiedRecipe {
    name: string;
    ingredients: SimplifiedIngredient[];
    macros?: {
        calories: number;
        protein: number;
        carbs: number;
        fat: number;
        fiber: number;
    };
}

export interface SimplifiedDay {
    name: string;
    meals: {
        breakfast: SimplifiedRecipe[];
        lunch: SimplifiedRecipe[];
        dinner: SimplifiedRecipe[];
        snack: SimplifiedRecipe[];
    };
    macros?: {
        calories: number;
        protein: number;
        carbs: number;
        fat: number;
        fiber: number;
    };
}

/**
 * Simplifies an OutgoingSearchResult (ingredient) to a basic JSON structure
 */
export const simplifyIngredient = (
    ingredient: OutgoingSearchResult
): SimplifiedIngredient => {
    const nutrition = ingredient.nutritionPreview;
    const portion = nutrition?.portion;

    // Get quantity and portion name
    const quantity = portion?.quantity || 1;
    const portionName = portion?.name || "serving";
    const quantityAndPortion = `${quantity} ${portionName}`;

    // Calculate total weight in grams
    const portionWeightValue = portion?.weight?.value || 100;
    const portionWeightUnit = portion?.weight?.unit || "g";

    // Convert to grams if needed
    let weightPerPortion = portionWeightValue;
    if (portionWeightUnit === "oz") {
        weightPerPortion = portionWeightValue * 28.35; // Convert oz to grams
    } else if (portionWeightUnit === "lb") {
        weightPerPortion = portionWeightValue * 453.592; // Convert lb to grams
    } else if (portionWeightUnit === "kg") {
        weightPerPortion = portionWeightValue * 1000; // Convert kg to grams
    }

    const totalWeightGrams =
        Math.round(weightPerPortion * quantity * 100) / 100;

    return {
        name: ingredient.displayName,
        brand: ingredient.brandName,
        quantityAndPortion,
        totalWeightGrams: Math.round(totalWeightGrams),
        macros: {
            calories: Math.round(nutrition?.calories || 0),
            protein: Math.round(nutrition?.protein || 0),
            carbs: Math.round(nutrition?.carbs || 0),
            fat: Math.round(nutrition?.fat || 0),
            fiber: Math.round(nutrition?.fiber || 0),
        },
    };
};

/**
 * Simplifies a recipe to include its name and simplified ingredients
 */
export const simplifyRecipe = (
    recipe: LLMRecipeOutgoing,
    includeMacros: boolean = false
): SimplifiedRecipe => {
    const simplifiedIngredients = recipe.ingredients.map(simplifyIngredient);

    const result: SimplifiedRecipe = {
        name: toTitleCase(recipe.name),
        ingredients: simplifiedIngredients,
    };

    if (includeMacros) {
        // Use the recipe's stored macros if available, otherwise calculate from ingredients
        if (recipe.macros && recipe.macros.calories > 0) {
            // Use stored recipe macros and round to integers for LLM efficiency
            result.macros = {
                calories: Math.round(recipe.macros.calories),
                protein: Math.round(recipe.macros.protein),
                carbs: Math.round(recipe.macros.carbs),
                fat: Math.round(recipe.macros.fat),
                fiber: Math.round(recipe.macros.fiber),
            };
        } else {
            // Fallback: Calculate total macros from ingredients
            const totalMacros = simplifiedIngredients.reduce(
                (
                    total: {
                        calories: number;
                        protein: number;
                        carbs: number;
                        fat: number;
                        fiber: number;
                    },
                    ingredient: SimplifiedIngredient
                ) => ({
                    calories: total.calories + ingredient.macros.calories,
                    protein: total.protein + ingredient.macros.protein,
                    carbs: total.carbs + ingredient.macros.carbs,
                    fat: total.fat + ingredient.macros.fat,
                    fiber: total.fiber + ingredient.macros.fiber,
                }),
                { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 }
            );

            result.macros = {
                calories: Math.round(totalMacros.calories),
                protein: Math.round(totalMacros.protein),
                carbs: Math.round(totalMacros.carbs),
                fat: Math.round(totalMacros.fat),
                fiber: Math.round(totalMacros.fiber),
            };
        }
    }

    return result;
};

/**
 * Simplifies a meal plan day to include its meals and simplified recipes
 */
export const simplifyDay = (
    day: LLMMealPlanOutgoing,
    dayName: string = "Day",
    includeMacros: boolean = false
): SimplifiedDay => {
    const result: SimplifiedDay = {
        name: dayName,
        meals: {
            breakfast: day.breakfast.recipes.map((recipe: LLMRecipeOutgoing) =>
                simplifyRecipe(recipe, includeMacros)
            ),
            lunch: day.lunch.recipes.map((recipe: LLMRecipeOutgoing) =>
                simplifyRecipe(recipe, includeMacros)
            ),
            dinner: day.dinner.recipes.map((recipe: LLMRecipeOutgoing) =>
                simplifyRecipe(recipe, includeMacros)
            ),
            snack: day.snack.recipes.map((recipe: LLMRecipeOutgoing) =>
                simplifyRecipe(recipe, includeMacros)
            ),
        },
    };

    if (includeMacros && day.macros) {
        result.macros = {
            calories: Math.round(day.macros.calories),
            protein: Math.round(day.macros.protein),
            carbs: Math.round(day.macros.carbs),
            fat: Math.round(day.macros.fat),
            fiber: Math.round(day.macros.fiber),
        };
    }

    return result;
};

/**
 * Helper function to get a formatted string representation of simplified data
 */
export const formatSimplifiedData = (
    data: SimplifiedIngredient | SimplifiedRecipe | SimplifiedDay
): string => {
    return JSON.stringify(data, null, 2);
};
